import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ReqResService } from '../../service/req-res.service';

declare var toastr: any;

@Component({
  selector: 'app-productitems',
  templateUrl: './productitems.component.html',
  styleUrls: ['./productitems.component.css']
})
export class ProductitemsComponent implements OnInit {
  productData: any[] = [];
  searchText: string = '';
  selectedGroupId: string = '';
  itemGroups: any[] = [];
  isLoading: boolean = false;
  isSyncing: boolean = false;
  syncingItemCode: string = '';
  currentPage: number = 1;
  pageSize: number = 50;
  defaultImage: string = '../assets/img/products/0.png';

  constructor(
    private reqRes: ReqResService,
    private titleService: Title
  ) {
    this.titleService.setTitle('Items');
  }

  ngOnInit(): void {
    this.loadProducts();
  }

  loadProducts(): void {
    this.isLoading = true;

    this.reqRes.getData('/Product/GetItemsList').subscribe(
      (data: any) => {
        this.productData = data || [];
        this.prepareGroups();
        this.currentPage = 1;
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        toastr.error('Items could not be loaded', '', { timeOut: 1600 });
        console.log(error);
      }
    );
  }

  syncProducts(): void {
    if (this.isSyncing) return;

    this.isSyncing = true;

    this.reqRes.postData('/Product/SyncItems', {}).subscribe(
      (data: any) => {
        this.isSyncing = false;

        if (data && data.success === false) {
          toastr.error(data.message || 'Item sync failed', '', { timeOut: 2200 });
          return;
        }

        const total = data && data.total != null ? data.total : 0;
        const inserted = data && data.inserted != null ? data.inserted : 0;
        const updated = data && data.updated != null ? data.updated : 0;
        const deactivated = data && data.deactivated != null ? data.deactivated : 0;
        const imageSync = data && data.imageSync ? data.imageSync : null;
        const imageText = imageSync
          ? ` Images: existing ${imageSync.existingImages || 0}, downloaded ${imageSync.savedFromSourcePath || 0}, endpoint ${imageSync.savedFromEndpoint || 0}, missing ${imageSync.missingSourcePath || 0}, failed ${imageSync.failed || 0}.`
          : '';

        console.log('Item sync result', data);
        toastr.success(`Synced ${total} products. ${inserted} new, ${updated} updated, ${deactivated} inactive.${imageText}`, '', { timeOut: 6000 });
        this.loadProducts();
      },
      error => {
        this.isSyncing = false;
        toastr.error('Item sync failed', '', { timeOut: 2200 });
        console.log(error);
      }
    );
  }

  syncProduct(item: any): void {
    if (this.isSyncing || this.syncingItemCode || !item?.itemCode) return;

    this.syncingItemCode = item.itemCode;

    this.reqRes.postData('/Product/SyncItem/' + encodeURIComponent(item.itemCode), {}).subscribe(
      (data: any) => {
        this.syncingItemCode = '';

        if (data && data.success === false) {
          toastr.error(data.message || 'Item sync failed', '', { timeOut: 2200 });
          return;
        }

        const inserted = data && data.inserted != null ? data.inserted : 0;
        const updated = data && data.updated != null ? data.updated : 0;
        const imageSync = data && data.imageSync ? data.imageSync : null;
        const imageText = imageSync
          ? ` Images: existing ${imageSync.existingImages || 0}, downloaded ${imageSync.savedFromSourcePath || 0}, failed ${imageSync.failed || 0}.`
          : '';

        console.log('Single item sync result', data);
        toastr.success(`${item.itemCode} synced. ${inserted} new, ${updated} updated.${imageText}`, '', { timeOut: 4000 });
        this.loadProducts();
      },
      error => {
        this.syncingItemCode = '';
        toastr.error('Item sync failed', '', { timeOut: 2200 });
        console.log(error);
      }
    );
  }

  prepareGroups(): void {
    const uniqueGroups: any[] = [];

    this.productData.forEach((item: any) => {
      const exists = uniqueGroups.some((g: any) => String(g.groupId) === String(item.groupId));

      if (!exists && item.groupId != null) {
        uniqueGroups.push({
          groupId: item.groupId,
          groupName: item.groupName
        });
      }
    });

    uniqueGroups.sort((a: any, b: any) =>
      (a.groupName || '').localeCompare(b.groupName || '')
    );

    this.itemGroups = uniqueGroups;
  }

  get filteredProducts(): any[] {
    let filtered = [...this.productData];

    if (this.selectedGroupId !== '' && this.selectedGroupId != null) {
      filtered = filtered.filter((p: any) => String(p.groupId) === String(this.selectedGroupId));
    }

    if (this.searchText && this.searchText.trim()) {
      const search = this.searchText.toLowerCase().trim();

      filtered = filtered.filter((p: any) =>
        (p.itemName && p.itemName.toLowerCase().includes(search)) ||
        (p.groupName && p.groupName.toLowerCase().includes(search)) ||
        (p.itemCode && p.itemCode.toLowerCase().includes(search))
      );
    }

    return filtered;
  }

  get pagedProducts(): any[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredProducts.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredProducts.length / this.pageSize) || 1;
  }

  get startItem(): number {
    if (this.filteredProducts.length === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItem(): number {
    const end = this.currentPage * this.pageSize;
    return end > this.filteredProducts.length ? this.filteredProducts.length : end;
  }

  get lastSyncDate(): string {
    const dates = this.productData
      .map((x: any) => x.lastSyncDate)
      .filter((x: any) => !!x)
      .sort();

    return dates.length ? dates[dates.length - 1] : '';
  }

  onFilterChange(): void {
    this.currentPage = 1;
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedGroupId = '';
    this.currentPage = 1;
  }

  onImageError(event: any): void {
    if (event?.target && event.target.src !== this.defaultImage) {
      event.target.src = this.defaultImage;
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  trackByProduct(index: number, item: any): any {
    return item && item.itemCode ? item.itemCode : index;
  }

  trackByGroup(index: number, item: any): any {
    return item && item.groupId ? item.groupId : index;
  }
}
